import type { MaterialDirNode, MaterialFilterType, MaterialType } from '@/lib/types/api'

// 扩展名白名单（与后端 / 前端 MATERIAL_EXTENSIONS 保持一致）。
// 用扩展名而非 MIME 通配（image/* / audio/*），避免 macOS/Windows 文件选择器
// 把 mp4 等视频文件混进音频选择框。
export const MATERIAL_TYPE_EXTENSIONS: Record<MaterialFilterType, string[]> = {
  all: [],
  image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico', 'tiff', 'tif', 'avif', 'heif', 'heic'],
  video: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm', 'm4v', 'mpg', 'mpeg', '3gp'],
  audio: ['mp3', 'wav', 'aac', 'ogg', 'flac', 'wma', 'm4a', 'ape', 'aiff'],
  file: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'md', 'csv', 'zip', 'rar', '7z'],
}

function extensionsToAccept(exts: string[]): string {
  return exts.map((ext) => `.${ext}`).join(',')
}

const MATERIAL_ACCEPT: Record<MaterialFilterType, string> = {
  all: 'image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.md,.zip,.rar,.7z',
  image: extensionsToAccept(MATERIAL_TYPE_EXTENSIONS.image),
  video: extensionsToAccept(MATERIAL_TYPE_EXTENSIONS.video),
  audio: extensionsToAccept(MATERIAL_TYPE_EXTENSIONS.audio),
  file: extensionsToAccept(MATERIAL_TYPE_EXTENSIONS.file),
}

export const MATERIAL_TYPE_LABELS: Record<MaterialType, string> = {
  image: '图片',
  video: '视频',
  audio: '音频',
  file: '文件',
}

/** 单个素材类型的展示名称；'all' 返回 null。 */
export function materialTypeLabel(type: MaterialFilterType | MaterialType): string | null {
  if (type === 'all') return null
  return MATERIAL_TYPE_LABELS[type] ?? null
}

/** 根据筛选类型生成「从资源库选择图片、视频」这类文案；未筛选时返回通用文案。 */
export function buildMaterialPickerTitle(materialTypes: MaterialFilterType[]): string {
  const names = materialTypes.map(materialTypeLabel).filter(Boolean)
  if (names.length === 0) return '从资源库选择素材'
  return `从资源库选择${names.join('、')}`
}

export function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / Math.pow(1024, idx)
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[idx]}`
}

export function getMaterialAccept(type: MaterialFilterType): string {
  return MATERIAL_ACCEPT[type] ?? '*'
}

export function flattenDirs(nodes: MaterialDirNode[], depth = 0) {
  const result: Array<{ id: string; label: string; depth: number; dirType?: string | null }> = []
  nodes.forEach((node) => {
    result.push({
      id: node.id,
      label: node.name,
      depth,
      dirType: node.dir_type ?? null,
    })
    if (node.children && node.children.length > 0) {
      result.push(...flattenDirs(node.children, depth + 1))
    }
  })
  return result
}

export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null
  const raw = window.localStorage.getItem('auth-storage')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    return parsed?.state?.token ?? null
  } catch {
    return null
  }
}

export function withAuthToken(url: string): string {
  const token = getAuthToken()
  if (!token) return url
  if (isExternalUrl(url)) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}token=${encodeURIComponent(token)}`
}

/**
 * Strip aliyun:// / tencent:// storage-key prefixes that are not real URLs.
 * These prefixes are used internally by the dashboard backend as storage identifiers;
 * they must never reach an <img src> or similar DOM attribute.
 */
export function fromStorageUrl(url: string): string {
  if (url.startsWith('aliyun://')) return url.slice('aliyun://'.length)
  if (url.startsWith('tencent://')) return url.slice('tencent://'.length)
  return url
}

/**
 * CDN 域名常量，用于 cdnImg() 将存储 key 转换到真实 URL。
 */
const CDN_DOMAIN_TENCENT = 'https://tcimg001.fkxiaoyu.com'
const CDN_DOMAIN_ALIYUN = 'https://img001.fkxiaoyu.com'

/**
 * 将 tencent:// / aliyun:// 内部存储 key 转换成可访问的 CDN 完整 URL。
 *
 *   tencent://material/...  →  https://tcimg001.fkxiaoyu.com/material/...
 *   aliyun://...            →  https://img001.fkxiaoyu.com/...
 *   不以 http 开头          →  https://img001.fkxiaoyu.com/...
 *   已经是 http(s)         →  原样返回
 */
export function cdnImg(url: string): string {
  if (!url) return url
  if (url.startsWith('tencent://')) return CDN_DOMAIN_TENCENT + '/' + url.slice('tencent://'.length)
  if (url.startsWith('aliyun://')) return CDN_DOMAIN_ALIYUN + '/' + url.slice('aliyun://'.length)
  if (!url.startsWith('http')) return CDN_DOMAIN_ALIYUN + '/' + url
  return url
}

/**
 * 压缩图片 URL — 为 CDN 域名追加缩放/格式参数。
 * 与 zipImageUrl 等价，保留别名以兼容项目习惯。
 */
export const zipImg = zipImageUrl

export function buildAbsoluteUrl(baseUrl: string, path?: string | null): string {
  if (!path) return ''

  // Strip storage-key prefixes (aliyun://, tencent://) so we can construct a real URL
  const cleanPath = fromStorageUrl(path)

  if (cleanPath !== path) {
    console.warn(
      '[buildAbsoluteUrl] Stripped storage prefix from URL; backend should provide frontUrlShow/fileUrlShow instead.',
      { original: path, cleaned: cleanPath },
    )
  }

  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
    return cleanPath
  }
  if (!baseUrl) return cleanPath
  const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const trimmedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`
  return `${trimmedBase}${trimmedPath}`
}

/** 判断 URL 是否指向 GIF 文件（忽略查询串/大小写）。GIF 压缩会变静态 webp，前后端统一跳过。 */
function isGifUrl(url: string): boolean {
  const path = url.split('?')[0].split('#')[0]
  return path.toLowerCase().endsWith('.gif')
}

export function zipImageUrl(url: string, style: 'w_200' | 'w_300' = 'w_200'): string {
  if (!url) return url
  if (isGifUrl(url)) return url
  // vault 图片域：缩略图 = 原 URL 同路径追加 /thumb（服务端出图，原图 URL 不受影响）
  if (url.includes('vault.fkxiaoyu.com') || url.includes('v-cdn1.fkxiaoyu.com')) {
    if (url.endsWith('/thumb')) return url
    const q = url.indexOf('?')
    return q === -1 ? `${url}/thumb` : `${url.slice(0, q)}/thumb${url.slice(q)}`
  }
  if (url.includes('tcimg001.fkxiaoyu.com')) {
    if (!url.includes('?') && style.startsWith('w_')) {
      const width = style.replace('w_', '')
      return `${url}?eo-img.resize=w/${width}&eo-img.format=webp`
    }
    return url
  }
  if (url.includes('img001.fkxiaoyu.com') && !url.includes('!')) {
    return `${url}!${style}`
  }
  return url
}

export function buildMaterialUrl(baseUrl: string, path?: string | null): string {
  return withAuthToken(buildAbsoluteUrl(baseUrl, path))
}

export function buildMaterialThumbnailUrl(baseUrl: string, path?: string | null): string {
  return withAuthToken(zipImageUrl(buildAbsoluteUrl(baseUrl, path), 'w_200'))
}

function isExternalUrl(url: string): boolean {
  if (!url || typeof window === 'undefined') return false
  try {
    const parsed = new URL(url)
    // 桌面版：本地 agent API 跑在 127.0.0.1/localhost，而前端可能来自
    // tauri://localhost 或 localhost:<port>，两者 host 不同但都属于本地服务。
    // 这类本地 URL 仍要附加 token —— <img>/<video> 无法带 Authorization 头，
    // 只能靠 URL 里的 token 通过 /file 的 @require_auth。
    if (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1') return false
    return parsed.hostname !== window.location.hostname
  } catch {
    return false
  }
}
